/**
 * Déclaration des régions (stacks API) disponibles pour la BO.
 *
 * Tout vient d'une seule variable d'env VITE_API_URLS (JSON stringifié), un
 * tableau d'objets RegionConfig. Ajouter/retirer une région = éditer la var
 * d'env de l'environnement cible, pas de changement de code.
 *
 * `storageProvider` n'est PAS exposé en UI : c'est uniquement l'info
 * envoyée au convertisseur pour qu'il choisisse le bon SDK S3 et le webhook
 * de l'API correspondante.
 */
export interface RegionConfig {
  /** Code court identifiant la région, stocké en localStorage. */
  code: string;
  /** Libellé affiché à l'utilisateur (ex: "EU", "US"). */
  label: string;
  /** URL de base de l'API pour cette région (doit se terminer par /). */
  baseUrl: string;
  /** Clé passée au convertisseur pour sélectionner le SDK S3 (aws | scw | …). */
  storageProvider: string;
}

function parseRegions(): RegionConfig[] {
  const raw = import.meta.env.VITE_API_URLS;
  if (!raw) {
    throw new Error(
      "VITE_API_URLS is missing. Set it to a JSON array of regions, e.g. " +
        '[{"code":"r1","label":"EU","baseUrl":"https://api.example.com/","storageProvider":"aws"}]',
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`VITE_API_URLS is not valid JSON: ${(err as Error).message}`);
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error("VITE_API_URLS must be a non-empty JSON array.");
  }
  return parsed
    .map((r, i) => {
      const { code, label, baseUrl, storageProvider } = (r ?? {}) as Partial<RegionConfig>;
      if (!code || !label || !baseUrl || !storageProvider) {
        throw new Error(
          `VITE_API_URLS[${i}] missing required field (code, label, baseUrl, storageProvider).`,
        );
      }
      return { code, label, baseUrl, storageProvider };
    })
    .filter((r) => r.baseUrl.length > 0);
}

/**
 * Ordre = priorité : la première région ayant répondu OK au login devient
 * la région active par défaut avant que l'user ne choisisse une instance.
 */
export const REGIONS: RegionConfig[] = parseRegions();

export function getRegion(code: string | null | undefined): RegionConfig | undefined {
  if (!code) return undefined;
  return REGIONS.find((r) => r.code === code);
}

/**
 * Retourne la région active (celle sélectionnée par l'utilisateur) ou la
 * première déclarée en fallback.
 */
export function getActiveRegion(): RegionConfig {
  const stored = localStorage.getItem("region");
  return getRegion(stored) ?? REGIONS[0];
}

export function tokenKeyFor(code: string): string {
  return `token_${code}`;
}
